<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Custom Images</h2>
        <div v-if="GlobalPolicy.edit()" class="flex justify-end gap-4">
            <FormButton color="blue"
                prefix-class="fak"
                icon="fa-link"
                @click="() => {
                    browser = true
                }">
                Attach
            </FormButton>
            <FormButton  color="blue"
                prefix-class="fak"
                icon="fa-upload"
                type="button" @click="() => {
                    uploads = true
                }">
                Upload
            </FormButton>
        </div>
    </div>

    <div v-if="parent.id !== null" class="sm:col-span-2">
        <Draggable :disabled="!GlobalPolicy.edit()" v-if="attachments.length > 0" :list="attachments" @start="" @end="dragend" itemKey="id" 
            class="image-list mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
            <template #item="{element, index}">
                <div :id="`item-${index}`" class="image-list-item group relative border-2 border-oakwood-gray-100 rounded p-5 flex flex-col bg-white" :class="[GlobalPolicy.edit() ? 'cursor-move' : 'cursor-no-drop' ]">
                    <div class="sm:block absolute top-2 right-2 p-2 bg-white rounded-full aspect-square z-10">
                        <button v-if="GlobalPolicy.edit()" type="button" @click="openDelete(element)" 
                            class="text-oakwood-gray-400 hover:text-oakwood-gray-500 focus:outline-none focus:text-oakwood-gray-500 transition ease-in-out duration-150"
                            aria-label="Detach this image" title="Detach this image"><span class="fa fa-close"></span>
                        </button>
                    </div>
                    <div
                        class="aspect-square overflow-hidden bg-oakwood-gray-200 lg:aspect-none group-hover:opacity-75 flex-none">
                        <router-link :to="{name: 'media-edit', params: {id: element?.media_id}}" class="cursor-pointer"><img :src="element?.media.url"
                            class="h-full w-full object-cover object-center lg:h-full lg:w-full border border-oakwood-gray-50"
                            :alt="element?.media.alt" :title="element?.media.caption">
                        </router-link>
                    </div>
                    <div class="mt-4 flex justify-between flex-1">
                        <div>
                            <h3 class="text-sm font-semibold text-bhav-blue-500">
                                {{ index + 1 }}: {{ element?.media?.alt }}
                            </h3>
                            <dl>
                                <dt class="sr-only">View</dt>
                                <dd
                                    class="bg-bhav-blue-500 text-white inline-block px-2 leading-5 rounded-full relative bottom-px text-xs">
                                    {{ element?.media?.meta?.position }}</dd>

                                <dt class="sr-only">Caption</dt>
                                <dd class="text-sm text-oakwood-gray-700">
                                    {{ element?.media?.caption }}
                                </dd>
                            </dl>
                        </div>
                    </div>
                    <div v-if="GlobalPolicy.edit()" class="mt-3 pt-3 border-t border-oakwood-gray-100 flex items-center gap-2">
                        <ToggleButton
                            :checked="element?.meta?.web_hero || false"
                            label="Web Hero"
                            @changed="(value) => toggleWebHero(element, value)" />
                        <span class="text-sm text-bhav-blue-300">Web Hero</span>
                    </div>
                </div>
            </template>
        </Draggable>
        <div v-else class="block sm:table-cell text-sm  text-bhav-blue-500 leading-5 px-6 py-4">
            There are currently no image items.
        </div>
    </div>

    <ModalDialog :open="browser" :no-padding="true" @close="() => { this.browser = false }">
        <template #header>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                    Custom Image Attachment
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                    Select an image from the media manager and attach it.
                </p>
            </div>
        </template>
        <template #body>
            <LoadingView v-if="isLoading" absolute="true" />
            <MediaBrowser :full="false" :communities="communities" :floorplans="floorplans" :keywords="keywords" :media="media" :pagination="pagination"
                @close="() => { this.browser = false }" @attach="prepare"
                @page-change="handlePageChange" @limit-change="handleLimitChange" @filter-change="handleFilterChange" @sort-change="handleSortChange"></MediaBrowser>
        </template>
    </ModalDialog>

    <AttachmentsUpload :open="uploads" @close="cancel" @save="prepare" :context="parent.id">
    </AttachmentsUpload>

    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg font-semibold">Confirm Image Detachment</h2>
        </template>
        <template #body>
            <p class="text-bhav-blue-500"><strong>Warning!</strong> You are about to detach <em>{{
                image?.media?.alt }}</em>. Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="drop" color="blue">
                Detach
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import FormButton from '@/components/form/FormButton.vue';
import ToggleButton from '@/components/form/ToggleButton.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import MediaBrowser from '@/components/MediaBrowser.vue';
import AttachmentsUpload from '@/components/AttachmentsUpload.vue';
import LoadingView from '@/components/LoadingView.vue';
import axios from 'axios';
import Draggable from 'vuedraggable';
import { GlobalPolicy } from '@/policies/global';

const image = {
    image: {
        id: null,
        path: null,
        alt: null,
        meta: {
            position: null
        }
    },
    type: 'URL'
}

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    emits: [
        'update'
    ],
    data() {
        return {
            attach: false,
            browser: false,
            uploads: false,
            index: false,
            image,
            selected: [],
            type: 'URL',
            deleting: false,
            imageRefresh: false,
            attachments: [],
            media: [],
            pagination: {
                start: 0,
                limit: 24,
                count: 0
            },
            currentSort: {
                column: null,
                direction: null,
            },
            communities: [],
            floorplans: [],
            keywords: [],
            isLoading: false,
        }
    },
    methods: {
        dragend() {
            axios.patch(`${this.context}s/${this.parent.id}/attachments`, {
                attachments: this.attachments
                    .map((attachment, i) => {
                        attachment.meta.order = i;
                        return attachment;
                    })
            })
            .catch(err => {
                console.error(err)
            })
        },
        indexCommunities() {
        axios
        .get(`divisions/communities`)
        .then((res) => {
            this.communities = res.data
            .map(community => {
                community.collections = community.collections
                .filter(collection => collection.status != 'inactive')
                .sort((a,b) => a.meta.info.name > b.meta.info.name ? 1 : -1);
                return community;
            })
            .filter(community => community.status != 'inactive' && community.collections.length > 0)
            .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
            this.indexFloorplans();
        })
        .catch((e) => {
            this.isLoading = false;
            throw e;
        });
        },
        indexFloorplans() {
        axios
        .get(`floorplans`)
        .then((res) => {
            this.floorplans = res.data
            this.indexKeywords();
        })
        .catch((e) => {
            this.isLoading = false;
            throw e;
        });
        },
        indexKeywords() {
        axios
        .get(`keywords`)
        .then((res) => {
            this.keywords = res.data
            this.isLoading = false;
        })
        .catch((e) => {
            this.isLoading = false;
            throw e;
        });
        },
        list() {
            if (!this.parent?.id) return;
            axios.get(`${this.context}s/${this.parent.id}/attachments`)
                .then(resp => {
                    this.attachments = resp.data;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        edit(index) {
            this.browser = true;
            this.index = index;
            this.image = this.parent.meta.images[index];
        },
        prepare(selected) {
            this.selected = selected;
            this.save();
        },
        save() {
            let next = this.attachments.length;

            let attachments = [
                ...this.attachments.map((attachment, i) => {
                    attachment.meta.order = i;
                    return attachment
                }),
                ...this.selected.map((attachment, i) =>
                ({
                    media_id: attachment.id,
                    item_id: this.parent.id,
                    item_type: this.context,
                    meta: {
                        type: this.type,
                        order: next + i
                    }
                })
                )
            ];

            axios.patch(`${this.context}s/${this.parent.id}/attachments`, { attachments })
                .then(resp => {
                    this.attachments = resp.data;
                });
            this.cancel();
        },
        drop() {
            if (this.index === false) {
                console.error('You must select an item to delete');
                return;
            }
            axios.delete(`attachments/${this.image.id}`)
                .then(resp => {
                    this.list();
                })
            this.cancel();
        },
        openDelete(image) {
            this.index = image.id;
            this.image = image
            this.deleting = true;
        },
        cancel() {
            this.attach = false;
            this.selected = [];
            this.browser = false;
            this.uploads = false;
            this.image = { ...image };
            this.index = false;
            this.deleting = false;
        },
        indexMedia(start = 0, filters = {}) {
            this.isLoading = true;
            const params = new URLSearchParams({
                start: start,
                limit: this.pagination.limit,
                ...(filters.category && { 'category': filters.category }),
                ...(filters.position && { 'position': filters.position }),
                ...(filters.attached && { 'attached': filters.attached }),
                ...(filters.search && { 'search': filters.search }),
                ...(this.currentSort.column && { 'sort_by': this.currentSort.column }),
                ...(this.currentSort.direction && { 'sort_dir': this.currentSort.direction }),
            });

            // Handle array parameters properly
            if (filters.keywords?.length) {
                filters.keywords.forEach(k => params.append('keywords[]', k));
            }
            if (filters.communities?.length) {
                filters.communities.forEach(c => params.append('communities[]', c));
            }
            if (filters.collections?.length) {
                filters.collections.forEach(c => params.append('collections[]', c));
            }
            if (filters.floorplans?.length) {
                filters.floorplans.forEach(f => params.append('floorplans[]', f));
            }

            // Exclude already attached media
            const excludeIds = this.attachments.map(attachment => attachment?.media?.id).filter(id => id);
            if (excludeIds.length) {
                excludeIds.forEach(id => params.append('exclude[]', id));
            }

            axios.get(`media?${params.toString()}`)
                .then((resp) => {
                    // Handle both old array format and new paginated format
                    if (Array.isArray(resp.data)) {
                        this.media = resp.data;
                    } else {
                        this.media = resp.data.data ?? [];
                        this.pagination = resp.data.pagination ?? this.pagination;
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    console.error(error);
                    this.isLoading = false;
                })
        },
        getAvailableImages() {
            this.indexMedia(0, {});
        },
        handlePageChange(page, filters) {
            const start = (page - 1) * this.pagination.limit;
            this.indexMedia(start, filters);
        },
        handleLimitChange(newLimit, filters) {
            this.pagination.limit = newLimit;
            this.indexMedia(0, filters);
        },
        handleFilterChange(filters) {
            this.indexMedia(0, filters);
        },
        handleSortChange(column, direction, filters) {
            this.currentSort.column = column;
            this.currentSort.direction = direction;
            // Reset to first page when sort changes
            this.indexMedia(0, filters);
        },
        toggleWebHero(attachment, value) {
            attachment.meta = {
                ...attachment.meta,
                web_hero: value
            };

            axios.patch(`attachments/${attachment.id}`, {
                meta: attachment.meta
            })
            .then(() => {
                // If setting to true, update local state to reflect that others are now false
                if (value) {
                    this.attachments.forEach(a => {
                        if (a.id !== attachment.id) {
                            a.meta.web_hero = false;
                        }
                    });
                }
                // Emit updated hero image
                const heroImage = this.attachments.find(a => a.meta?.web_hero === true) || this.attachments[0];
                this.$emit('update', heroImage);
            })
            .catch(err => {
                console.error(err);
                // Revert on error
                attachment.meta.web_hero = !value;
            });
        }
    },
    computed: {
        getItemId() {
            return this.parent.id;
        },
        valid() {
            if(this.selected.length == 0) return false;
            return true;
        }
    },
    created() {
        this.list();
        this.indexCommunities();
    },
    watch: {
        browser: {
            handler(newVal) {
                if (newVal === true) {
                    this.getAvailableImages();
                }
            }
        },
        attachments: {
            handler() {
                if(this.attachments.length) {
                    // Emit web_hero attachment if exists, otherwise first attachment
                    const heroImage = this.attachments.find(a => a.meta?.web_hero === true) || this.attachments[0];
                    this.$emit('update', heroImage);
                }
            }
        }
    },
    props: {
        parent: {
            type: Object,
            required: true
        },
        context: {
            type: String,
            required: true
        }
    },
    components: {
        FormButton,
        ToggleButton,
        ModalDialog,
        MediaBrowser,
        AttachmentsUpload,
        LoadingView,
        Draggable
    }
}
</script>